import apiClient from '@/lib/api';
import { Place } from '@/types/place';
import { User, UserFilters } from '@/types/user';
import { DashboardStats, AdminPlaceFilters, CountryStats, DateStats, StatusStats } from '@/types/admin';

export const adminApi = {
  // Place Management
  /**
   * Get all places for admin (with status filter).
   */
  getAllPlaces: (filters?: AdminPlaceFilters): Promise<Place[]> => {
    return apiClient.get('/api/admin/places', { params: filters }).then(res => res.data.data || res.data);
  },

  /**
   * Approve a place.
   */
  approvePlace: (placeId: string): Promise<Place> => {
    return apiClient.put(`/api/admin/places/${placeId}/approve`).then(res => res.data.data || res.data);
  },

  /**
   * Reject a place.
   */
  rejectPlace: (placeId: string): Promise<Place> => {
    return apiClient.put(`/api/admin/places/${placeId}/reject`).then(res => res.data.data || res.data);
  },

  // Dashboard Analytics
  /**
   * Get dashboard statistics.
   */
  getDashboardStats: (): Promise<DashboardStats> => {
    return apiClient.get('/api/admin/reports/dashboard').then(res => res.data.data || res.data);
  },

  /**
   * Get places per country statistics.
   */
  getPlacesPerCountry: (): Promise<CountryStats[]> => {
    return apiClient.get('/api/admin/reports/placespercountry').then(res => {
      const data = res.data.data || res.data;
      // Backend returns { placesPerCountry: [{ _id: country, totalPlaces: count }] }
      let array: any[] = [];
      if (data && typeof data === 'object' && 'placesPerCountry' in data) {
        array = (data as any).placesPerCountry || [];
      } else if (Array.isArray(data)) {
        array = data;
      }
      // Transform backend format { _id: country, totalPlaces: count } to { country: string, count: number }
      return array.map(item => ({
        country: item._id || item.country || '',
        count: item.totalPlaces || item.count || 0,
      }));
    });
  },

  /**
   * Get users created by date statistics.
   */
  getUsersCreatedByDate: (): Promise<DateStats[]> => {
    return apiClient.get('/api/admin/reports/userscreatedate').then(res => {
      const data = res.data.data || res.data;
      // Backend returns { usersPerCreationDate: [{ _id: date, count: count }] }
      let array: any[] = [];
      if (data && typeof data === 'object' && 'usersPerCreationDate' in data) {
        array = (data as any).usersPerCreationDate || [];
      } else if (Array.isArray(data)) {
        array = data;
      }
      // Transform backend format { _id: date, count: count } to { date: string, count: number }
      return array.map(item => ({
        date: item._id || item.date || '',
        count: item.count || 0,
      }));
    });
  },

  /**
   * Get places per status statistics.
   */
  getPlacesPerStatus: (): Promise<StatusStats[]> => {
    return apiClient.get('/api/admin/reports/placeperstatus').then(res => {
      const data = res.data.data || res.data;
      // Backend returns { placesPerStatus: [{ _id: status, count: count }] }
      let array: any[] = [];
      if (data && typeof data === 'object' && 'placesPerStatus' in data) {
        array = (data as any).placesPerStatus || [];
      } else if (Array.isArray(data)) {
        array = data;
      }
      // Transform backend format { _id: status, count: count } to { status: string, count: number }
      return array.map(item => ({
        status: item._id || item.status || '',
        count: item.count || 0,
      }));
    });
  },

  /**
   * Get places with rating count.
   */
  getPlacesWithRating: (): Promise<number> => {
    return apiClient.get('/api/admin/reports/placewithrating').then(res => {
      const data = res.data.data || res.data;
      // Backend returns { placesWithRating: [{ _id, ratingsCount, averageRating }, ...] }
      // Extract the array and return its length (count of places with ratings)
      if (data && typeof data === 'object' && 'placesWithRating' in data) {
        const array = (data as any).placesWithRating || [];
        return Array.isArray(array) ? array.length : 0;
      }
      // If it's already a number, return it
      if (typeof data === 'number') {
        return data;
      }
      // If it's an array, return its length
      if (Array.isArray(data)) {
        return data.length;
      }
      return 0;
    });
  },

  /**
   * Get total users count.
   */
  getUsersTotal: (): Promise<{ totalUsers: number }> => {
    return apiClient.get('/api/admin/reports/userstotal').then(res => res.data.data || res.data);
  },

  /**
   * Get total places count.
   */
  getPlacesTotal: (): Promise<{ totalPlaces: number }> => {
    return apiClient.get('/api/admin/reports/placestotal').then(res => res.data.data || res.data);
  },

  /**
   * Get favorites count.
   */
  getFavoritesCount: (): Promise<{ favoritesCount: number }> => {
    return apiClient.get('/api/admin/reports/favoritescount').then(res => res.data.data || res.data);
  },

  // User Management
  /**
   * Get all users (admin only, with optional filters).
   */
  getAllUsers: (filters?: UserFilters): Promise<User[]> => {
    return apiClient.get('/api/users', { params: filters }).then(res => res.data.data || res.data);
  },

  /**
   * Update user (admin only).
   */
  updateUser: (userId: string, data: Partial<User>): Promise<User> => {
    return apiClient.put(`/api/users/${userId}`, data).then(res => res.data.data || res.data);
  },

  /**
   * Delete user (admin only).
   */
  deleteUser: (userId: string): Promise<void> => {
    return apiClient.delete(`/api/users/${userId}`).then(() => undefined);
  },
};
